import { defineComponent } from 'vue';

import type { Offer, Package } from '@/@types/graphql-types';
import type {
	VisibilityChangedPayload,
	BatchImpressionTrackingOptions,
} from '@/helpers/composables/useBatchImpressionTracking';
import { useBatchImpressionTracking } from '@/helpers/composables/useBatchImpressionTracking';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

export type ImpressionTrackingOfferDetails = Pick<
	Offer,
	'id' | 'monetizationType' | 'presentationType' | 'retailPriceValue' | 'currency' | 'lastChangeRetailPriceValue'
> & {
	package: Pick<Package, 'packageId' | 'clearName'> & { technicalName?: Package['technicalName'] };
	isLeavingIconShown?: boolean;
	bundleId?: number;
	bundleTechnicalName?: string;
};

export type OfferVisibilityChangedPayload = VisibilityChangedPayload<ImpressionTrackingOfferDetails>;

export const observableOptions = {
	once: false,
	throttle: 300,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.5, // 50% visibility of each element
	},
} as const;

export function useOfferImpressionTracking(options: BatchImpressionTrackingOptions = {}) {
	return useBatchImpressionTracking<ImpressionTrackingOfferDetails>({
		category: 'offer',
		toContext: getOfferImpressionContext,
		observableOptions,
		...options,
	});
}

/** Renderless version */
export default defineComponent({
	name: 'UseOfferImpressionTracking',
	setup(props, { slots }) {
		return () => slots.default?.(useOfferImpressionTracking(props)) ?? slots[0];
	},
});

export function getOfferImpressionContext(offerDetails: ImpressionTrackingOfferDetails) {
	if (offerDetails?.bundleId) {
		// package.technicalName is available only for bundles
		return TrackingHelper.getPackageContext(
			offerDetails.package.packageId,
			offerDetails.package.technicalName ?? '',
			'bundle',
			offerDetails.bundleId,
			offerDetails.bundleTechnicalName
		);
	}

	const contentType = offerDetails?.isLeavingIconShown ? ClickoutContentType.LeavingSoon : undefined;
	return SnowplowClickoutContextGraphql.fromProviderOffer(offerDetails, contentType);
}
